import { AnyAction, isRejectedWithValue, Middleware } from '@reduxjs/toolkit';

import { InspectorConsoleSource } from 'ev-common/Inspector/InspectorTypes';
import { signOutRedirect } from 'ev-hooks/signOut';
import { appendConsoleLine } from 'ev-store/actions';
import Environments from 'ev-types/environments';
import { isObject, isString } from 'ev-utils/types';

export const SESSION_TIMEOUT_ERROR_MESSAGE =
  'Your session expired. Please sign in again to continue.';

const isARejectedWithValueAction = (anyAction: unknown) =>
  isRejectedWithValue(anyAction);

export type ErrorPayload = {
  status?: number;
  data: {
    error?: { message: string } | string;
    error_codes?: string[];
    errors?: string[] | Record<string, string>;
    message?: string;
    /** CDX proxy responses nest the upstream body here */
    data?: unknown;
  };
};

type ActionWithPayload = {
  payload: ErrorPayload;
};

type ActionWithData = ActionWithPayload & {
  data: {
    handled: boolean;
  };
};

export const isActionPayload = (payload: unknown): payload is ErrorPayload =>
  isObject(payload) && isObject(payload.data);

const isActionWithPayload = (action: unknown): action is ActionWithPayload =>
  isObject(action) &&
  isObject(action.payload) &&
  isActionPayload(action.payload);

const isActionWithData = (action: unknown): action is ActionWithData =>
  isObject(action) && isObject(action.data) && isActionWithPayload(action);

const getError = (payload: ErrorPayload) => {
  let message = '';

  const { data, status } = payload;

  if (isObject(data.error) && data.error.message) {
    message = data.error.message;
  } else if (isString(data.error)) {
    message = data.error;
  } else if (isString(data.message)) {
    message = data.message;
  } else if (data.error_codes) {
    message = data.error_codes.join(', ');
  } else if (data.errors) {
    if (isObject(data.errors)) {
      message = Object.values(data.errors).flat().join(', ');
    } else if (isString(data.errors)) {
      message = data.errors;
    } else {
      message = data.errors.join(', ');
    }
  }

  return { message, status };
};

export const errorLogger: Middleware = api => next => (action: AnyAction) => {
  if (isARejectedWithValueAction(action)) {
    let error;
    if (isActionWithPayload(action) && isActionPayload(action.payload)) {
      error = getError(action.payload);
      api.dispatch(
        appendConsoleLine({
          source: InspectorConsoleSource.RTK,
          message: 'Error',
          data: {
            error: action.payload,
            rtkInfo: {
              endpoint: (action.meta.arg as Record<string, string> | undefined)
                ?.endpointName,
              httpCode: error.status,
              rtkType: action.type,
            },
          },
        }),
      );
      if (
        isActionWithData(action) &&
        action.data.handled !== true &&
        action.payload.status === 401
      ) {
        void signOutRedirect(null);
        return;
      }
    }
    if (process.env.NODE_ENV !== Environments.Test) {
      console.error(
        error ? `Error ${error.status}: ${error.message}` : 'Unknown error',
      );
    }
  }
  return next(action);
};
